// Custom Trails Storage (Stage 6j)
// ================================
//
// Device-local Trails, die der User im Create-Custom-Flow gebaut hat.
// Leben NUR im IDB, kein Server-Sync — jeder User hat seine eigenen. Die
// Routes persistieren zwischen App-Sessions und überleben PWA-Restarts.
//
// Schema: siehe storage/db.ts customTrails-Store. Key = client-generierte id.

import { getDb } from './storage/db';

export const CUSTOM_TRAILS_UPDATED_EVENT = 'adt:custom-trails-updated';

/**
 * Wave 14ca-fix64: pro-Segment-Trail-Mapping persistieren, damit wir beim
 * Oeffnen einer gespeicherten Custom Tour das Hoehenprofil nach Trail-
 * Difficulty einfaerben + die Trail-Liste mit Click-to-Highlight bauen
 * koennen. Der Pin-Route-Endpoint liefert das schon im response
 * (`routeSegments`), wir haben es nur bisher beim Save weggeworfen.
 *
 * `kind`:
 *   - 'trail'     → Segment liegt auf einem bekannten Trail (trailId set)
 *   - 'connector' → OSM-Edge zwischen Trails (trailId null)
 *   - 'bridge'    → kuenstliche Verbindung wenn Routing-Algo Luecken
 *                    fuellen musste (trailId null)
 *
 * `from` / `to` sind Indices in das `coords`-Array — von und bis
 * inklusive. Cumulative-distance-Anywhere-Mapping passiert beim Render.
 */
export interface CustomTrailRouteSegment {
  kind: 'connector' | 'trail' | 'bridge';
  trailId: string | null;
  from: number;
  to: number;
}

/**
 * Tupel-Format für CustomTrail.coords:
 *   - [lon, lat]               — vor Wave 14cf-fix5 (kein Elevation)
 *   - [lon, lat, eleMeters]    — ab Wave 14cf-fix5 wenn Elevation bekannt
 *
 * Reader müssen `c[2]` als optional behandeln (undefined = kein ele).
 * Writer dürfen die 3-Tupel-Form nutzen wenn sie die Höhe haben (GPX-
 * Import, Routing-Engine-Output mit Elevation, etc.).
 */
export type CustomTrailCoord = [number, number] | [number, number, number];

export interface CustomTrail {
  id: string;
  name: string;
  difficulty: string; // 'unknown' initial — kann später editiert werden
  coords: CustomTrailCoord[];
  lengthM: number;
  trailsUsed: string[];
  trailIds?: string[];
  startPin?: [number, number];
  endPin?: [number, number];
  createdAt: string;
  /** Wave 14ca-fix64: Pro-Segment-Trail-Mapping. Optional — alte
   *  CustomTrails (vor 14ca-fix64) haben das Feld nicht; UI muss damit
   *  umgehen koennen (zeigt dann nur die normale durchgehende Profil-
   *  Linie ohne Trail-Faerbung). */
  routeSegments?: CustomTrailRouteSegment[];
  /** Wave 14cf-fix11: "Auf Karte anzeigen"-Toggle.
   *  Wenn true, wird die Tour permanent als dünne sky-blue Linie
   *  gerendert — auch wenn sie nicht aktiv selektiert ist. Default
   *  false. Mehrere Tours können gleichzeitig sichtbar sein. */
  showOnMap?: boolean;
  /** Wave 14cf-fix12: km-Marker-Carousel.
   *   - 'off' (default): keine Distanz-Beschriftungen
   *   - '1km':           Label alle 1 km entlang der Linie
   *   - '10km':          Label alle 10 km entlang der Linie
   *  Wirkt nur wenn showOnMap=true. UI in der Liste/Detail-Page tappt
   *  durchs Carousel: off → 1km → 10km → off. */
  kmMarkers?: 'off' | '1km' | '10km';
  /** ULTRA: Herkunft der Tour. 'router' = per „Route berechnen"-A→B-Planer
   *  erzeugt (asphaltierte Rennrad-Route), sonst undefined (hand-gezeichnet /
   *  Pin-Route / GPX-Import). Rein informativ — erlaubt später, berechnete
   *  Routen von hand-gebauten zu unterscheiden. Alte Tours haben das Feld
   *  nicht; Reader müssen undefined tolerieren. */
  source?: string;
}

function makeId(): string {
  // Simpler Client-ID-Generator — Timestamp + Random-Slug.
  // Klassisches ms-timestamp + kurze Zufalls-Hex damit Kollisionen bei
  // Millisekunden-genauen Doppel-Saves vermieden werden.
  const ms = Date.now();
  const rnd = Math.floor(Math.random() * 0xffff)
    .toString(16)
    .padStart(4, '0');
  return `c${ms}-${rnd}`;
}

function dispatchUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(CUSTOM_TRAILS_UPDATED_EVENT));
  } catch {
    /* SSR — egal */
  }
}

export async function saveCustomTrail(input: {
  name: string;
  /** Coords als 2-Tupel oder 3-Tupel (mit Elevation in Metern als
   *  drittem Element). Wenn die Quelle Elevation hat (GPX-Import,
   *  Routing mit DEM-Snap), unbedingt 3-Tupel benutzen — sonst muss
   *  das Profil-Widget auf SRTM zurückfallen, was bei Custom-Routen
   *  spürbar ungenauer ist (User-Bug 14cf-fix5: Komoot 1010 m HM →
   *  App 1734 m HM weil ele nicht persistiert wurde). */
  coords: CustomTrailCoord[];
  lengthM: number;
  trailsUsed?: string[];
  trailIds?: string[];
  startPin?: [number, number];
  endPin?: [number, number];
  difficulty?: string;
  /** Wave 14ca-fix64: pro-Segment-Trail-Mapping aus pin-route-response. */
  routeSegments?: CustomTrailRouteSegment[];
  /** ULTRA: 'router' für per A→B-Planer berechnete Routen. */
  source?: string;
}): Promise<CustomTrail> {
  const db = await getDb();
  const record: CustomTrail = {
    id: makeId(),
    name: (input.name || 'Custom route').trim(),
    difficulty: input.difficulty || 'unknown',
    coords: input.coords,
    lengthM: input.lengthM,
    trailsUsed: input.trailsUsed || [],
    trailIds: input.trailIds,
    startPin: input.startPin,
    endPin: input.endPin,
    createdAt: new Date().toISOString(),
    routeSegments: input.routeSegments,
    source: input.source,
  };
  await db.put('customTrails', record);
  dispatchUpdated();
  schedulePushCustomTrails();
  return record;
}

export async function listCustomTrails(): Promise<CustomTrail[]> {
  const db = await getDb();
  const all = await db.getAll('customTrails');
  // Neueste zuerst.
  return all.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function getCustomTrail(id: string): Promise<CustomTrail | null> {
  const db = await getDb();
  const row = await db.get('customTrails', id);
  return row || null;
}

export async function deleteCustomTrail(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('customTrails', id);
  dispatchUpdated();
  schedulePushCustomTrails();
}

export async function renameCustomTrail(
  id: string,
  newName: string,
): Promise<CustomTrail | null> {
  const db = await getDb();
  const row = await db.get('customTrails', id);
  if (!row) return null;
  row.name = newName.trim() || row.name;
  await db.put('customTrails', row);
  dispatchUpdated();
  schedulePushCustomTrails();
  return row;
}

/**
 * Wave 14cf-fix18: Reverse-Tour. Speichert eine neue Custom Tour mit
 * umgedrehter Fahrtrichtung — non-destructive, das Original bleibt
 * erhalten. Der User kann so eine Tour von Z nach A fahren, ohne den
 * Create-Custom-Flow nochmal durchlaufen zu müssen.
 *
 * Was umgedreht wird:
 *  - coords: einfach `.reverse()`. Höhen-Tupel-Form bleibt erhalten.
 *  - routeSegments: jedes Segment {from, to} bekommt remappte Indices
 *    (`(N-1)-to` / `(N-1)-from`), und die Segment-Reihenfolge wird
 *    umgedreht, damit sie weiter in Coord-Traversierungs-Reihenfolge ist.
 *  - startPin ↔ endPin werden getauscht.
 *  - trailsUsed / trailIds werden umgedreht, damit die Trail-Liste in
 *    der Reihenfolge bleibt, in der sie auf der Tour begegnet werden.
 *  - name bekommt " (reversed)" angehängt.
 *  - lengthM bleibt identisch (Geometrie unverändert).
 *  - difficulty/showOnMap/kmMarkers werden geerbt; die User-eigenen
 *    UI-Toggles soll der User bewusst auf der neuen Kopie setzen.
 */
export async function reverseCustomTrail(
  id: string,
): Promise<CustomTrail | null> {
  const db = await getDb();
  const source = await db.get('customTrails', id);
  if (!source) return null;

  const n = source.coords.length;
  const reversedCoords = source.coords.slice().reverse();

  const reversedSegments = source.routeSegments
    ? source.routeSegments
        .map((s) => ({
          kind: s.kind,
          trailId: s.trailId,
          from: n - 1 - s.to,
          to: n - 1 - s.from,
        }))
        .reverse()
    : undefined;

  const reversed: CustomTrail = {
    ...source,
    id: makeId(),
    name: `${source.name} (reversed)`,
    coords: reversedCoords,
    routeSegments: reversedSegments,
    startPin: source.endPin,
    endPin: source.startPin,
    trailsUsed: source.trailsUsed.slice().reverse(),
    trailIds: source.trailIds ? source.trailIds.slice().reverse() : undefined,
    createdAt: new Date().toISOString(),
  };

  await db.put('customTrails', reversed);
  dispatchUpdated();
  schedulePushCustomTrails();
  return reversed;
}

/**
 * Wave 14cf-fix11: Toggle "Auf Karte anzeigen". Setzt das Flag, persistiert
 * und feuert das CUSTOM_TRAILS_UPDATED_EVENT damit die MapPage instant
 * re-rendert. Returnt die aktualisierte CustomTrail oder null wenn die
 * id nicht existiert.
 */
export async function setCustomTrailShowOnMap(
  id: string,
  showOnMap: boolean,
): Promise<CustomTrail | null> {
  const db = await getDb();
  const row = await db.get('customTrails', id);
  if (!row) return null;
  row.showOnMap = showOnMap;
  await db.put('customTrails', row);
  dispatchUpdated();
  schedulePushCustomTrails();
  return row;
}

/**
 * Wave 14cf-fix12: km-Marker-Carousel-Setter. Setzt den Wert (off/1km/10km)
 * direkt; der Caller (List/Detail-Page) ist fuer die Carousel-Logik
 * (next-state) zustaendig — saubere Trennung State vs. UX-Pattern.
 */
export type KmMarkers = 'off' | '1km' | '10km';

export async function setCustomTrailKmMarkers(
  id: string,
  value: KmMarkers,
): Promise<CustomTrail | null> {
  const db = await getDb();
  const row = await db.get('customTrails', id);
  if (!row) return null;
  row.kmMarkers = value;
  await db.put('customTrails', row);
  dispatchUpdated();
  schedulePushCustomTrails();
  return row;
}

/**
 * Wave 14cf-fix15: Difficulty einer Custom Tour aendern.
 *
 * UI-Wunsch: User soll die Schwierigkeit nachträglich editieren können
 * (z.B. weil der "härtester einbezogener Trail"-Default nicht zur
 * gefuehlten Tour-Schwierigkeit passt). Die Skala ist die normale
 * Difficulty-Skala (Difficulty-Type aus @adtu/shared) — wir validieren
 * hier NICHT gegen die Liste, weil die UI-Schicht ein <select> aus den
 * gleichen Werten baut. Falls jemand eine ungueltige String einsetzt,
 * faerbt der Renderer auf Fallback (gray).
 */
export async function setCustomTrailDifficulty(
  id: string,
  difficulty: string,
): Promise<CustomTrail | null> {
  const db = await getDb();
  const row = await db.get('customTrails', id);
  if (!row) return null;
  row.difficulty = difficulty;
  await db.put('customTrails', row);
  dispatchUpdated();
  schedulePushCustomTrails();
  return row;
}

/** Helper: nächste Stufe im Carousel. */
export function nextKmMarkerState(current: KmMarkers | undefined): KmMarkers {
  switch (current) {
    case '1km':
      return '10km';
    case '10km':
      return 'off';
    case 'off':
    default:
      return '1km';
  }
}

export async function getCustomTrailsCount(): Promise<number> {
  const db = await getDb();
  return db.count('customTrails');
}

// ─── Cross-device sync (Wave 14ca-fix30) ────────────────────────────────────
//
// Strategie: voller Mirror via PUT /api/me/custom-trails (full-replace).
//   - Beim App-Start: pullCustomTrails() holt vom Server, merged in IDB
//     (server gewinnt für gleiche IDs, Mac-only IDs bleiben).
//   - Nach lokalen Änderungen (save/delete/rename): pushCustomTrails()
//     debounced ~2s und schickt den ganzen IDB-State hoch.
//
// Konflikt-Resolution: keine. Bei 2 Geräten die gleichzeitig schreiben
// gewinnt das letzte. Für <30 Custom-Trails pro User mit wenigen aktiven
// Devices ist das ein akzeptabler Trade-off vs. echtem CRDT.

let pushTimer: ReturnType<typeof setTimeout> | null = null;

export function schedulePushCustomTrails(): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(async () => {
    pushTimer = null;
    try {
      const trails = await listCustomTrails();
      await fetch('/api/me/custom-trails', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trails }),
      });
    } catch {
      /* offline → nächste Änderung versucht's wieder */
    }
  }, 2000);
}

/**
 * Vom Server holen + in IDB mergen. Server gewinnt für IDs die beide
 * Seiten kennen. Mac-only IDs (z.B. neu lokal angelegt vor App-Start
 * konnte sync'en) bleiben erhalten.
 */
export async function pullCustomTrails(): Promise<void> {
  let serverTrails: CustomTrail[];
  try {
    const res = await fetch('/api/me/custom-trails', { credentials: 'include' });
    if (!res.ok) return; // 401 = nicht eingeloggt → silent
    const data = (await res.json()) as { trails?: CustomTrail[] };
    serverTrails = Array.isArray(data?.trails) ? data.trails : [];
  } catch {
    return; // offline
  }

  const db = await getDb();
  const localTrails = await db.getAll('customTrails');
  const localIds = new Set(localTrails.map((t) => t.id));

  // Server-Versionen schreiben/überschreiben in IDB.
  for (const t of serverTrails) {
    await db.put('customTrails', t);
  }

  // Wenn der Server welche hat die wir lokal NICHT hatten (= cross-device
  // Trail, anderes Gerät hat ihn angelegt), dispatchen wir ein Event damit
  // die Custom-Trails-Liste sich neu lädt.
  const serverIds = new Set(serverTrails.map((t) => t.id));
  const hasNewFromServer = [...serverIds].some((id) => !localIds.has(id));
  // Wenn der Server welche NICHT mehr hat die wir lokal noch hatten, müssen
  // wir noch entscheiden: löschen wir lokal? Sicherer ist: nein, lokale
  // Versionen behalten und beim nächsten Push wieder hochschicken (so verliert
  // der User nichts, falls auf einem anderen Gerät jemand alles weggeputzt
  // hat). Wenn der User das WIRKLICH löschen wollte, muss er's nochmal
  // explizit auf diesem Gerät tun.

  if (hasNewFromServer || serverTrails.length > 0) {
    dispatchUpdated();
  }
}
